import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';

import { FieldsEntityInstanceResponse } from '@models/api';
import { FieldTypeEnum } from '@models/module/fields/enums';
import { FormFieldModel, FormFieldValueType, FormValueType } from '@models/module/fields/main';
import { DmFormField, DmGridFields } from '@module-instance/dynamic-module/common/models/dm-grid-fields.model';
import { ModuleInstanceService } from '@module-instance/services';
import { CipoFieldConfig, FieldsService, FormItem, RelatedListParent } from '@shared/components/fields/common';
import { Observable } from 'rxjs';

export type DmFieldsParams = {
  moduleId: number;
  contractId: number;
  workflowId: number;
  entityInstanceId: number;
  screenId?: number;
  editMode?: boolean;
  transitionId?: number;
  actionInstanceId?: number;
  sovEntityInstanceId?: number;
};

@Injectable({
  providedIn: 'root',
})
export class DmFieldsService {
  private moduleInstanceService = inject(ModuleInstanceService);
  private fieldsService = inject(FieldsService);

  getGridFields(params: DmFieldsParams, clone = false) {
    const request = clone
      ? this.moduleInstanceService.getFieldsEntityInstanceToDuplicate({ ...params, editMode: true })
      : this.moduleInstanceService.getFieldsEntityInstance({ ...params, editMode: true });
    return this.mapGridFields(request, params);
  }

  applyFieldValues(fields: DmFormField[], values: FormValueType): DmFormField[] {
    const fieldsWithValues = fields.map(field => ({
      ...field,
      value: this.fieldsService.mapValue(values[field.id], field.fieldOptions) ?? field.value,
    }));

    return fieldsWithValues.map(field => {
      const dataSourceInfo = field.fieldOptions.dataSourceInfo;
      if (!dataSourceInfo?.parents?.length) {
        return field;
      }

      const parents = dataSourceInfo.parents.map(parent => {
        const parentValue = fieldsWithValues.find(f => Number(f.id) === parent.parentFieldId)?.value;
        return {
          ...parent,
          parentElementIds: this.toStringArray(parentValue),
        };
      });
      const hasMissingParent = parents.some(parent => !parent.parentElementIds.length);

      return {
        ...field,
        fieldOptions: {
          ...field.fieldOptions,
          options: hasMissingParent ? [] : null,
          dataSourceInfo: {
            ...dataSourceInfo,
            parents,
          },
        },
      };
    });
  }

  mapGridFields(request: Observable<FieldsEntityInstanceResponse>, params: DmFieldsParams): Observable<DmGridFields> {
    return request.pipe(
      map(
        ({
          autosaveEnabled,
          fields,
          canChangeAssignment,
          emails,
          emailsDataSource,
          hideRolesNotify,
          manualAssignUsers,
          roleToExcludeIds,
          requiresNonBindingSignature,
          workflowGroups,
          screenLabel,
          isDraft,
        }) => {
          return {
            autosaveEnabled,
            fields: this.mapFields(fields, params, isDraft),
            emails,
            canChangeAssignment,
            /* Take out the 'isUsed' property because it shows the 'In use' label in the Select options */
            emailsDataSource: emailsDataSource?.map(({ isUsed, ...rest }) => ({ ...rest })),
            hideRolesNotify,
            manualAssignUsers,
            roleToExcludeIds,
            requiresNonBindingSignature,
            workflowGroups,
            screenLabel,
          } as DmGridFields;
        },
      ),
    );
  }

  private mapFields(
    fields: FormFieldModel[],
    params: Partial<DmFieldsParams>,
    isDraft: boolean,
  ): FormItem<FormFieldValueType, CipoFieldConfig>[] {
    const newFields = sortFieldsByPosition(fields);
    return newFields.map(({ triggeredFields, ...field }) => {
      const formItem = this.mapField(field, params, isDraft);
      /* For related fields we need to populate parents and childs */
      if (field.parentFieldIds?.length || field.childFieldIds?.length) {
        formItem.fieldOptions.dataSourceInfo.parents = this.getRelatedFieldParents(fields, field);
        formItem.fieldOptions.dataSourceInfo.childFieldIds = field.childFieldIds;
      }
      return {
        ...formItem,
        triggeredFields,
      };
    });
  }

  private mapField(
    field: FormFieldModel,
    params: Partial<DmFieldsParams>,
    isDraft: boolean,
  ): FormItem<FormFieldValueType, CipoFieldConfig> {
    const { contractId, moduleId, entityInstanceId, actionInstanceId } = params;
    const formItem = this.fieldsService.mapFormFieldModel(field, isDraft);
    /* Relation fields need additional dataSourceInfo to get the proper dataSource */
    if (field.type === FieldTypeEnum.Relation) {
      formItem.fieldOptions.dataSourceInfo = {
        ...formItem.fieldOptions.dataSourceInfo,
        contractId,
        moduleId,
        entityInstanceId,
        fieldId: Number(formItem.id),
      };
    }
    /* make these props available */
    formItem.fieldOptions.entityInstanceId = entityInstanceId;
    formItem.fieldOptions.actionInstanceId = actionInstanceId;
    formItem.fieldOptions.moduleId = moduleId;
    formItem.fieldOptions.contractId = contractId;

    if (formItem.fieldOptions.dataSourceInfo) {
      formItem.fieldOptions.dataSourceInfo.sovEntityInstanceId = params.sovEntityInstanceId;
    }

    return formItem;
  }

  /* For related fields we need to populate parentFieldIds */
  private getRelatedFieldParents(fields: FormFieldModel[], currentField: FormFieldModel): RelatedListParent[] {
    return currentField.parentFieldIds.map(parentFieldId => {
      const parentFieldValue = this.getFieldById(fields, parentFieldId).value as string | string[];
      return {
        parentFieldId,
        parentElementIds: this.toStringArray(parentFieldValue),
      };
    });
  }

  private toStringArray(value: FormFieldValueType): string[] {
    if (Array.isArray(value)) {
      return value.map(item => String(item));
    }
    return value === null || value === undefined || value === '' ? [] : [String(value)];
  }

  private getFieldById(fields: FormFieldModel[], fieldId: number) {
    return fields.find(f => Number(f.id) === fieldId);
  }
}

export const sortFieldsByPosition = (fields: FormFieldModel[]) => {
  return [...fields].sort((a, b) => {
    if (a.y !== b.y) {
      return a.y - b.y; // top to bottom
    }

    return a.x - b.x; // left to right within the same row
  });
};
